import { Point } from './types'
import { MapElement, MapSettings, CurvedEdge } from '../../../types'
import { drawWrappedText } from './textWrapping'

// Image cache to prevent flickering
const imageCache = new Map<string, HTMLImageElement>()

const getOrLoadImage = (src: string): HTMLImageElement | null => {
  if (!src) return null

  let img = imageCache.get(src)
  if (!img) {
    img = new Image()
    img.src = src
    imageCache.set(src, img)

    // Force re-render when image loads
    img.onload = () => {
      // Trigger a re-render by dispatching a custom event
      window.dispatchEvent(new CustomEvent('imageLoaded'))
    }
  }

  return img
}

// Helper function to calculate default control points for a Bézier curve
const calculateDefaultControlPoints = (
  startPoint: Point,
  endPoint: Point,
  curvature: number,
) => {
  // Calculate perpendicular direction for curve offset

  // Calculate perpendicular vector
  const dx = endPoint.x - startPoint.x
  const dy = endPoint.y - startPoint.y
  const perpX = -dy
  const perpY = dx

  // Normalize perpendicular vector
  const length = Math.sqrt(perpX * perpX + perpY * perpY)
  const normalizedPerpX = perpX / length
  const normalizedPerpY = perpY / length

  // Scale based on curvature (0-100) and edge length - make it more pronounced
  const curveDistance = (curvature / 100) * (length * 0.5)

  // Calculate control points offset from the line
  const offsetX = normalizedPerpX * curveDistance
  const offsetY = normalizedPerpY * curveDistance

  // Position control points at 1/3 and 2/3 along the edge with perpendicular offset
  const controlPoint1 = {
    x: startPoint.x + dx * 0.33 + offsetX,
    y: startPoint.y + dy * 0.33 + offsetY,
  }

  const controlPoint2 = {
    x: startPoint.x + dx * 0.66 + offsetX,
    y: startPoint.y + dy * 0.66 + offsetY,
  }

  return { controlPoint1, controlPoint2 }
}

// Helper function to draw a Bézier curved edge between two points
const drawCurvedEdge = (
  ctx: CanvasRenderingContext2D,
  startPoint: Point,
  endPoint: Point,
  controlPoint1: Point,
  controlPoint2: Point,
) => {
  ctx.bezierCurveTo(
    controlPoint1.x,
    controlPoint1.y,
    controlPoint2.x,
    controlPoint2.y,
    endPoint.x,
    endPoint.y,
  )
}

const drawWallElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isSelected: boolean,
  isOnCurrentFloor: boolean,
) => {
  if (!element.properties.endPosition) return

  const thickness = element.properties.thickness || 6
  const material = element.properties.material || 'drywall'
  const isLoadBearing = element.properties.loadBearing || false

  ctx.lineWidth = thickness

  // Draw different patterns based on material
  switch (material) {
    case 'brick':
      ctx.strokeStyle = '#8B4513'
      ctx.setLineDash([8, 4])
      break
    case 'concrete':
      ctx.strokeStyle = '#696969'
      ctx.setLineDash([12, 2, 4, 2])
      break
    case 'glass':
      ctx.strokeStyle = '#87CEEB'
      ctx.setLineDash([2, 6])
      break
    case 'metal':
      ctx.strokeStyle = '#C0C0C0'
      ctx.setLineDash([4, 4])
      break
    default: // drywall/wood frame
      ctx.strokeStyle = element.properties.color || '#8B4513'
      ctx.setLineDash([])
  }

  ctx.beginPath()
  ctx.moveTo(element.position.x, element.position.y)
  ctx.lineTo(element.properties.endPosition.x, element.properties.endPosition.y)
  ctx.stroke()
  ctx.setLineDash([])

  // Draw load-bearing indicator
  if (isLoadBearing) {
    ctx.fillStyle = '#FF4500'
    ctx.font = 'bold 10px Arial'
    ctx.textAlign = 'center'
    const midX = (element.position.x + element.properties.endPosition.x) / 2
    const midY = (element.position.y + element.properties.endPosition.y) / 2
    ctx.fillText('LB', midX, midY - thickness / 2 - 5)
    ctx.textAlign = 'left'
  }

  // Draw label if present
  if (element.properties.label) {
    ctx.fillStyle = '#333'
    ctx.font = '12px Arial'
    ctx.textAlign = 'center'

    const midX = (element.position.x + element.properties.endPosition.x) / 2
    const midY = (element.position.y + element.properties.endPosition.y) / 2

    // Apply label position offset if available
    const labelOffsetX = element.labelPosition?.x || 0
    const labelOffsetY = element.labelPosition?.y || 0
    const labelX = midX + labelOffsetX
    const labelY = midY + labelOffsetY

    ctx.fillText(
      element.properties.label,
      labelX,
      labelY + (isLoadBearing ? 10 : 0),
    )
    ctx.textAlign = 'left'
  }
}

const drawDoorElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isSelected: boolean,
  isOnCurrentFloor: boolean,
) => {
  if (!element.properties.endPosition) return

  const width = element.properties.width || 36 // 3 feet default
  const thickness = 4

  // Draw door frame
  ctx.lineWidth = thickness
  ctx.beginPath()
  ctx.moveTo(element.position.x, element.position.y)
  ctx.lineTo(element.properties.endPosition.x, element.properties.endPosition.y)
  ctx.stroke()

  // Draw door swing/operation based on door type
  const dx = element.properties.endPosition.x - element.position.x
  const dy = element.properties.endPosition.y - element.position.y
  const angle = Math.atan2(dy, dx)
  const swingInward = element.properties.swingDirection === 'inward'
  const doorType = element.properties.doorType || 'single'

  ctx.setLineDash([3, 3])
  ctx.lineWidth = 1
  ctx.strokeStyle = element.properties.color || '#D2691E'

  switch (doorType) {
    case 'double':
      // Double door - two swing arcs meeting in the middle
      const halfWidth = width / 2

      ctx.beginPath()
      if (swingInward) {
        // Left door arc
        ctx.arc(
          element.position.x,
          element.position.y,
          halfWidth,
          angle,
          angle + Math.PI / 2,
        )
        // Move to right door starting point
        ctx.moveTo(
          element.properties.endPosition.x + Math.cos(angle) * halfWidth,
          element.properties.endPosition.y + Math.sin(angle) * halfWidth,
        )
        // Right door arc
        ctx.arc(
          element.properties.endPosition.x,
          element.properties.endPosition.y,
          halfWidth,
          angle + Math.PI,
          angle + Math.PI / 2,
          true,
        )
      } else {
        // Left door arc (outward)
        ctx.arc(
          element.position.x,
          element.position.y,
          halfWidth,
          angle,
          angle - Math.PI / 2,
          true,
        )
        // Move to right door starting point
        ctx.moveTo(
          element.properties.endPosition.x +
            Math.cos(angle + Math.PI) * halfWidth,
          element.properties.endPosition.y +
            Math.sin(angle + Math.PI) * halfWidth,
        )
        // Right door arc (outward)
        ctx.arc(
          element.properties.endPosition.x,
          element.properties.endPosition.y,
          halfWidth,
          angle + Math.PI,
          angle + (3 * Math.PI) / 2,
        )
      }
      ctx.stroke()
      break

    case 'sliding':
      // Sliding door - parallel lines showing slide direction
      const perpAngle = angle + Math.PI / 2
      const slideOffset = 10

      ctx.beginPath()
      // Main door position
      ctx.moveTo(element.position.x, element.position.y)
      ctx.lineTo(
        element.properties.endPosition.x,
        element.properties.endPosition.y,
      )

      // Slide track indication
      const slideX1 = element.position.x + Math.cos(perpAngle) * slideOffset
      const slideY1 = element.position.y + Math.sin(perpAngle) * slideOffset
      const slideX2 =
        element.properties.endPosition.x + Math.cos(perpAngle) * slideOffset
      const slideY2 =
        element.properties.endPosition.y + Math.sin(perpAngle) * slideOffset

      ctx.moveTo(slideX1, slideY1)
      ctx.lineTo(slideX2, slideY2)
      ctx.stroke()
      break

    case 'folding':
      // Folding door - zigzag pattern
      const segments = 4 // Number of fold segments
      const segmentLength = width / segments

      ctx.beginPath()
      for (let i = 0; i < segments; i++) {
        const segmentAngle = angle + (i % 2 === 0 ? Math.PI / 6 : -Math.PI / 6)
        const startRadius = i * segmentLength
        const endRadius = (i + 1) * segmentLength

        if (swingInward) {
          ctx.arc(
            element.position.x,
            element.position.y,
            startRadius,
            angle,
            segmentAngle,
          )
          ctx.arc(
            element.position.x,
            element.position.y,
            endRadius,
            segmentAngle,
            angle,
          )
        } else {
          ctx.arc(
            element.position.x,
            element.position.y,
            startRadius,
            angle,
            segmentAngle,
          )
          ctx.arc(
            element.position.x,
            element.position.y,
            endRadius,
            segmentAngle,
            angle,
          )
        }
      }
      ctx.stroke()
      break

    case 'pocket':
      // Pocket door - shows slide into wall
      const pocketDepth = 15
      const wallAngle = angle + Math.PI / 2

      ctx.beginPath()
      // Door in pocket position (parallel to wall)
      const pocketX1 = element.position.x + Math.cos(wallAngle) * pocketDepth
      const pocketY1 = element.position.y + Math.sin(wallAngle) * pocketDepth
      const pocketX2 =
        element.properties.endPosition.x + Math.cos(wallAngle) * pocketDepth
      const pocketY2 =
        element.properties.endPosition.y + Math.sin(wallAngle) * pocketDepth

      ctx.moveTo(pocketX1, pocketY1)
      ctx.lineTo(pocketX2, pocketY2)

      // Arrow showing slide direction
      const arrowSize = 5
      const pocketMidX = (pocketX1 + pocketX2) / 2
      const pocketMidY = (pocketY1 + pocketY2) / 2
      const doorMidX =
        (element.position.x + element.properties.endPosition.x) / 2
      const doorMidY =
        (element.position.y + element.properties.endPosition.y) / 2

      ctx.moveTo(doorMidX, doorMidY)
      ctx.lineTo(pocketMidX, pocketMidY)

      // Arrow head
      ctx.moveTo(pocketMidX - arrowSize, pocketMidY - arrowSize)
      ctx.lineTo(pocketMidX, pocketMidY)
      ctx.lineTo(pocketMidX - arrowSize, pocketMidY + arrowSize)
      ctx.stroke()
      break

    default: // single door
      ctx.beginPath()
      if (swingInward) {
        // Swing inward - arc on opposite side (corrected)
        ctx.arc(
          element.position.x,
          element.position.y,
          width,
          angle - Math.PI / 2,
          angle,
        )
      } else {
        // Swing outward - arc on one side (corrected)
        ctx.arc(
          element.position.x,
          element.position.y,
          width,
          angle,
          angle + Math.PI / 2,
        )
      }
      ctx.stroke()
      break
  }

  ctx.setLineDash([])

  // Draw door type indicator
  if (element.properties.doorType) {
    ctx.fillStyle = '#666'
    ctx.font = '10px Arial'
    ctx.textAlign = 'center'
    const midX = (element.position.x + element.properties.endPosition.x) / 2
    const midY = (element.position.y + element.properties.endPosition.y) / 2

    let typeText = ''
    switch (element.properties.doorType) {
      case 'double':
        typeText = 'DBL'
        break
      case 'sliding':
        typeText = 'SLD'
        break
      case 'folding':
        typeText = 'FOLD'
        break
      case 'pocket':
        typeText = 'PKT'
        break
      default:
        typeText = ''
        break
    }

    if (typeText) {
      ctx.fillText(typeText, midX, midY - 8)
    }
    ctx.textAlign = 'left'
  }

  // Draw label if present
  if (element.properties.label) {
    ctx.fillStyle = '#333'
    ctx.font = '12px Arial'
    ctx.textAlign = 'center'

    const midX = (element.position.x + element.properties.endPosition.x) / 2
    const midY = (element.position.y + element.properties.endPosition.y) / 2

    // Apply label position offset if available
    const labelOffsetX = element.labelPosition?.x || 0
    const labelOffsetY = element.labelPosition?.y || 0
    const labelX = midX + labelOffsetX
    const labelY = midY + labelOffsetY

    ctx.fillText(
      element.properties.label,
      labelX,
      labelY + (element.properties.doorType ? 5 : -8),
    )
    ctx.textAlign = 'left'
  }
}

const drawWindowElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isSelected: boolean,
  isOnCurrentFloor: boolean,
) => {
  if (!element.properties.endPosition) return

  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(element.position.x, element.position.y)
  ctx.lineTo(element.properties.endPosition.x, element.properties.endPosition.y)
  ctx.stroke()

  // Draw window frame lines based on window type
  const dx = element.properties.endPosition.x - element.position.x
  const dy = element.properties.endPosition.y - element.position.y
  const length = Math.sqrt(dx * dx + dy * dy)
  const windowType = element.properties.windowType || 'double-hung'

  ctx.strokeStyle = element.properties.color || '#4169E1'
  ctx.lineWidth = 1

  switch (windowType) {
    case 'double-hung':
    case 'casement':
      // Traditional panes
      const segments = Math.max(2, Math.floor(length / 30))
      for (let i = 1; i < segments; i++) {
        const t = i / segments
        const x = element.position.x + dx * t
        const y = element.position.y + dy * t

        ctx.beginPath()
        ctx.moveTo(x - (dy / length) * 8, y + (dx / length) * 8)
        ctx.lineTo(x + (dy / length) * 8, y - (dx / length) * 8)
        ctx.stroke()
      }
      break
    case 'bay':
      // Draw multiple lines for bay window
      ctx.setLineDash([2, 2])
      for (let i = 0; i < 3; i++) {
        const offset = (i - 1) * 3
        ctx.beginPath()
        ctx.moveTo(element.position.x + offset, element.position.y + offset)
        ctx.lineTo(
          element.properties.endPosition.x + offset,
          element.properties.endPosition.y + offset,
        )
        ctx.stroke()
      }
      ctx.setLineDash([])
      break
    case 'sliding':
      // Draw sliding tracks
      const midX = (element.position.x + element.properties.endPosition.x) / 2
      const midY = (element.position.y + element.properties.endPosition.y) / 2
      ctx.beginPath()
      ctx.moveTo(midX - (dy / length) * 12, midY + (dx / length) * 12)
      ctx.lineTo(midX + (dy / length) * 12, midY - (dx / length) * 12)
      ctx.stroke()
      break
  }

  // Draw glazing indicator
  if (
    element.properties.glazing === 'double' ||
    element.properties.glazing === 'triple'
  ) {
    ctx.setLineDash([1, 2])
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(element.position.x + 2, element.position.y + 2)
    ctx.lineTo(
      element.properties.endPosition.x + 2,
      element.properties.endPosition.y + 2,
    )
    ctx.stroke()
    ctx.setLineDash([])
  }

  // Draw label if present
  if (element.properties.label) {
    ctx.fillStyle = '#333'
    ctx.font = '12px Arial'
    ctx.textAlign = 'center'

    const midX = (element.position.x + element.properties.endPosition.x) / 2
    const midY = (element.position.y + element.properties.endPosition.y) / 2

    // Apply label position offset if available
    const labelOffsetX = element.labelPosition?.x || 0
    const labelOffsetY = element.labelPosition?.y || 0
    const labelX = midX + labelOffsetX
    const labelY = midY + labelOffsetY

    ctx.fillText(element.properties.label, labelX, labelY - 10)
    ctx.textAlign = 'left'
  }
}

const drawRoomElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isSelected: boolean,
  isOnCurrentFloor: boolean,
  mapSettings: MapSettings,
) => {
  if (!element.properties.width || !element.properties.height) return

  // Handle background image or fill color
  if (element.properties.backgroundImage) {
    ctx.save()
    ctx.beginPath()
    ctx.rect(
      element.position.x,
      element.position.y,
      element.properties.width,
      element.properties.height,
    )
    ctx.clip() // Clip to room shape

    // Get cached image
    const img = getOrLoadImage(element.properties.backgroundImage)

    // Apply background image opacity if set
    const oldAlpha = ctx.globalAlpha
    ctx.globalAlpha = element.properties.backgroundImageOpacity ?? 1

    // Draw based on fit mode
    const fitMode = element.properties.backgroundImageFit || 'cover'
    if (img && img.complete) {
      // Check for custom size
      const customWidth = element.properties.backgroundImageWidth
      const customHeight = element.properties.backgroundImageHeight
      const offsetX = element.properties.backgroundImageX || 0
      const offsetY = element.properties.backgroundImageY || 0

      if (fitMode === 'repeat') {
        // Handle repeat mode (with or without custom size)
        // Create a temporary canvas to draw the sized image
        const tempCanvas = document.createElement('canvas')
        const tempCtx = tempCanvas.getContext('2d')
        if (tempCtx) {
          // Set temp canvas size to the custom size or original image size
          tempCanvas.width = customWidth || img.width
          tempCanvas.height = customHeight || img.height
          tempCtx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height)

          const pattern = ctx.createPattern(tempCanvas, 'repeat')
          if (pattern) {
            // Save current transform
            ctx.save()
            // Translate to align pattern with room position
            ctx.translate(element.position.x + offsetX, element.position.y + offsetY)
            ctx.fillStyle = pattern
            ctx.beginPath()
            ctx.rect(
              0,
              0,
              element.properties.width,
              element.properties.height,
            )
            ctx.fill()
            ctx.restore()
          }
        }
      } else if (customWidth && customHeight) {
        // Use custom size and position for non-repeat modes
        ctx.drawImage(
          img,
          element.position.x + offsetX,
          element.position.y + offsetY,
          customWidth,
          customHeight
        )
      } else {
        switch (fitMode) {
        case 'contain':
          const scale = Math.min(
            element.properties.width / img.width,
            element.properties.height / img.height
          )
          const scaledWidth = img.width * scale
          const scaledHeight = img.height * scale
          const x = element.position.x + (element.properties.width - scaledWidth) / 2
          const y = element.position.y + (element.properties.height - scaledHeight) / 2
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight)
          break
        case 'stretch':
          ctx.drawImage(
            img,
            element.position.x,
            element.position.y,
            element.properties.width,
            element.properties.height
          )
          break
        case 'cover':
        default:
          const coverScale = Math.max(
            element.properties.width / img.width,
            element.properties.height / img.height
          )
          const coverWidth = img.width * coverScale
          const coverHeight = img.height * coverScale
          const coverX = element.position.x + (element.properties.width - coverWidth) / 2
          const coverY = element.position.y + (element.properties.height - coverHeight) / 2
          ctx.drawImage(img, coverX, coverY, coverWidth, coverHeight)
          break
        }
      }
    }

    ctx.globalAlpha = oldAlpha
    ctx.restore()
  } else if (element.properties.fillColor && element.properties.fillColor !== 'transparent') {
    // Draw fill if specified
    ctx.fillStyle = element.properties.fillColor
    ctx.beginPath()
    ctx.rect(
      element.position.x,
      element.position.y,
      element.properties.width,
      element.properties.height,
    )
    ctx.fill()
  }

  // Only draw stroke if strokeWidth > 0
  const strokeWidth = element.properties.strokeWidth ?? 2
  if (strokeWidth > 0) {
    ctx.lineWidth = strokeWidth
    if (!isSelected) {
      ctx.strokeStyle = element.properties.color || '#4a90e2'
      ctx.setLineDash([5, 5])
    }
    ctx.beginPath()
    ctx.rect(
      element.position.x,
      element.position.y,
      element.properties.width,
      element.properties.height,
    )
    ctx.stroke()
    ctx.setLineDash([])
  }

  // Draw room label with text wrapping
  if (element.properties.roomName && !element.properties.hideLabel) {
    const fontSize = element.properties.fontSize || 16

    // Apply label position offset if available
    const labelOffsetX = element.labelPosition?.x || 0
    const labelOffsetY = element.labelPosition?.y || 0

    // Apply rotation if specified
    if (element.properties.labelRotation) {
      ctx.save()
      const centerX = element.position.x + element.properties.width / 2 + labelOffsetX
      const centerY = element.position.y + element.properties.height / 2 + labelOffsetY
      ctx.translate(centerX, centerY)
      ctx.rotate((element.properties.labelRotation * Math.PI) / 180)

      // Draw wrapped text centered at 0,0 after rotation
      drawWrappedText(
        ctx,
        element.properties.roomName,
        -element.properties.width / 2,
        -element.properties.height / 2,
        element.properties.width,
        element.properties.height,
        fontSize,
        '#333',
        false
      )

      // Calculate and display dimensions and area (rotated with label)
      if (element.measurements?.realWidth && element.measurements?.realLength) {
        // Use the actual realArea if available (accounts for curved edges), otherwise calculate from width*length
        const area =
          element.measurements.realArea ||
          element.measurements.realWidth * element.measurements.realLength
        const unitAbbrev = mapSettings.units === 'feet' ? 'ft' : 'm'
        const areaUnit = mapSettings.units === 'feet' ? 'sq ft' : 'sq m'

        ctx.font = '11px Arial'
        ctx.fillText(
          `${element.measurements.realWidth.toFixed(
            1,
          )} × ${element.measurements.realLength.toFixed(1)} ${unitAbbrev}`,
          0,
          16,
        )
        ctx.fillText(`${area.toFixed(1)} ${areaUnit}`, 0, 30)
      }
      ctx.restore()
    } else {
      // Draw wrapped text without rotation and get bottom position
      const textBottom = drawWrappedText(
        ctx,
        element.properties.roomName,
        element.position.x + labelOffsetX,
        element.position.y + labelOffsetY,
        element.properties.width,
        element.properties.height,
        fontSize,
        '#333',
        false
      )

      // Calculate and display dimensions and area right below text
      if (element.measurements?.realWidth && element.measurements?.realLength && !element.properties.hideMeasurements) {
        // Use the actual realArea if available (accounts for curved edges), otherwise calculate from width*length
        const area =
          element.measurements.realArea ||
          element.measurements.realWidth * element.measurements.realLength
        const unitAbbrev = mapSettings.units === 'feet' ? 'ft' : 'm'
        const areaUnit = mapSettings.units === 'feet' ? 'sq ft' : 'sq m'

        ctx.fillStyle = '#666'
        ctx.font = '11px system-ui, -apple-system, sans-serif'
        ctx.textAlign = 'center'
        const centerX = element.position.x + element.properties.width / 2 + labelOffsetX

        // Position measurements 10px below the text
        const measurementY = textBottom + 15

        ctx.fillText(
          `${element.measurements.realWidth.toFixed(1)} × ${element.measurements.realLength.toFixed(1)} ${unitAbbrev}`,
          centerX,
          measurementY
        )
        ctx.fillText(`${area.toFixed(1)} ${areaUnit}`, centerX, measurementY + 14)
      }
    }
    ctx.textAlign = 'left'
  }
}

const drawPolygonElement = (
  ctx: CanvasRenderingContext2D,
  element: MapElement,
  isSelected: boolean,
  isOnCurrentFloor: boolean,
  mapSettings: MapSettings,
) => {
  if (!element.properties.vertices || element.properties.vertices.length < 3)
    return

  const vertices = element.properties.vertices as Point[]
  const curvedEdges = element.curvedEdges || []

  // Create a map for quick lookup of curved edges
  const curvedEdgeMap = new Map<number, CurvedEdge>()
  curvedEdges.forEach(edge => {
    curvedEdgeMap.set(edge.edgeIndex, edge)
  })

  ctx.beginPath()
  ctx.moveTo(vertices[0].x, vertices[0].y)

  // Draw each edge (either straight or curved)
  for (let i = 0; i < vertices.length; i++) {
    const nextIndex = (i + 1) % vertices.length
    const curvedEdge = curvedEdgeMap.get(i)

    if (curvedEdge) {
      // Draw Bézier curved edge
      try {
        // Use stored control points if available, otherwise calculate defaults
        let cp1 = curvedEdge.controlPoint1
        let cp2 = curvedEdge.controlPoint2

        // If control points are not set or are zeros, calculate them from curvature
        if (
          !cp1 ||
          !cp2 ||
          (cp1.x === 0 && cp1.y === 0) ||
          (cp2.x === 0 && cp2.y === 0)
        ) {
          const defaultPoints = calculateDefaultControlPoints(
            vertices[i],
            vertices[nextIndex],
            curvedEdge.curvature ?? 50,
          )
          cp1 = defaultPoints.controlPoint1
          cp2 = defaultPoints.controlPoint2
        }

        drawCurvedEdge(ctx, vertices[i], vertices[nextIndex], cp1, cp2)
      } catch (error) {
        // If curve drawing fails, fall back to straight line
        ctx.lineTo(vertices[nextIndex].x, vertices[nextIndex].y)
      }
    } else {
      // Draw straight edge
      ctx.lineTo(vertices[nextIndex].x, vertices[nextIndex].y)
    }
  }

  ctx.closePath()

  // Handle background image or fill color
  if (element.properties.backgroundImage) {
    ctx.save()
    ctx.clip() // Clip to polygon shape

    // Get cached image
    const img = getOrLoadImage(element.properties.backgroundImage)

    // Find bounding box of polygon
    let minX = vertices[0].x, maxX = vertices[0].x
    let minY = vertices[0].y, maxY = vertices[0].y
    vertices.forEach(v => {
      minX = Math.min(minX, v.x)
      maxX = Math.max(maxX, v.x)
      minY = Math.min(minY, v.y)
      maxY = Math.max(maxY, v.y)
    })
    const width = maxX - minX
    const height = maxY - minY

    // Apply background image opacity if set
    const oldAlpha = ctx.globalAlpha
    ctx.globalAlpha = element.properties.backgroundImageOpacity ?? 1

    // Draw based on fit mode
    const fitMode = element.properties.backgroundImageFit || 'cover'
    if (img && img.complete) {
      // Check for custom size
      const customWidth = element.properties.backgroundImageWidth
      const customHeight = element.properties.backgroundImageHeight
      const offsetX = element.properties.backgroundImageX || 0
      const offsetY = element.properties.backgroundImageY || 0

      if (fitMode === 'repeat') {
        // Handle repeat mode (with or without custom size)
        // Create a temporary canvas to draw the sized image
        const tempCanvas = document.createElement('canvas')
        const tempCtx = tempCanvas.getContext('2d')
        if (tempCtx) {
          // Set temp canvas size to the custom size or original image size
          tempCanvas.width = customWidth || img.width
          tempCanvas.height = customHeight || img.height
          tempCtx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height)

          const pattern = ctx.createPattern(tempCanvas, 'repeat')
          if (pattern) {
            // Save current transform
            ctx.save()
            // Translate to align pattern with polygon bounds
            ctx.translate(minX + offsetX, minY + offsetY)
            ctx.fillStyle = pattern
            // Recreate the polygon path for filling (adjusting for translation)
            ctx.beginPath()
            ctx.moveTo(vertices[0].x - minX - offsetX, vertices[0].y - minY - offsetY)
            for (let i = 1; i < vertices.length; i++) {
              ctx.lineTo(vertices[i].x - minX - offsetX, vertices[i].y - minY - offsetY)
            }
            ctx.closePath()
            ctx.fill()
            ctx.restore()
          }
        }
      } else if (customWidth && customHeight) {
        // Use custom size and position for non-repeat modes
        ctx.drawImage(
          img,
          element.position.x + offsetX,
          element.position.y + offsetY,
          customWidth,
          customHeight
        )
      } else {
        switch (fitMode) {
        case 'contain':
          const scale = Math.min(width / img.width, height / img.height)
          const scaledWidth = img.width * scale
          const scaledHeight = img.height * scale
          const x = minX + (width - scaledWidth) / 2
          const y = minY + (height - scaledHeight) / 2
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight)
          break
        case 'stretch':
          ctx.drawImage(img, minX, minY, width, height)
          break
        case 'cover':
        default:
          const coverScale = Math.max(width / img.width, height / img.height)
          const coverWidth = img.width * coverScale
          const coverHeight = img.height * coverScale
          const coverX = minX + (width - coverWidth) / 2
          const coverY = minY + (height - coverHeight) / 2
          ctx.drawImage(img, coverX, coverY, coverWidth, coverHeight)
          break
        }
      }
    }

    ctx.globalAlpha = oldAlpha
    ctx.restore()
  } else if (element.properties.fillColor && element.properties.fillColor !== 'transparent') {
    // Fill polygon if fill color is specified
    ctx.fillStyle = element.properties.fillColor
    ctx.fill()
  }

  // Only draw stroke if strokeWidth > 0
  const strokeWidth = element.properties.strokeWidth ?? 2
  if (strokeWidth > 0) {
    ctx.lineWidth = strokeWidth
    if (!isSelected) {
      ctx.strokeStyle = element.properties.color || '#4a90e2'
      ctx.setLineDash([5, 5])
    } else {
      ctx.strokeStyle = element.properties.color || '#4a90e2'
    }
    ctx.stroke()
    ctx.setLineDash([])
  }

  // Draw polygon name and measurements like room
  if (element.properties.roomName || element.properties.label) {
    // Calculate centroid for default label placement
    let defaultCenterX = 0,
      defaultCenterY = 0
    vertices.forEach(vertex => {
      defaultCenterX += vertex.x
      defaultCenterY += vertex.y
    })
    defaultCenterX /= vertices.length
    defaultCenterY /= vertices.length

    // Apply label position offset if available
    const labelOffsetX = element.labelPosition?.x || 0
    const labelOffsetY = element.labelPosition?.y || 0
    const labelX = defaultCenterX + labelOffsetX
    const labelY = defaultCenterY + labelOffsetY

    ctx.font = 'bold 14px Arial'
    ctx.textAlign = 'center'

    // Apply rotation if specified
    if (element.properties.labelRotation) {
      ctx.save()
      ctx.translate(labelX, labelY)
      ctx.rotate((element.properties.labelRotation * Math.PI) / 180)

      const labelText = element.properties.roomName || element.properties.label || 'Polygon'

      // Draw white outline first
      ctx.strokeStyle = 'white'
      ctx.lineWidth = 4
      ctx.lineJoin = 'round'
      ctx.strokeText(labelText, 0, 0)

      // Then draw the fill text
      ctx.fillStyle = '#333'
      ctx.fillText(labelText, 0, 0)

      // Calculate and display area for polygon (rotated with label)
      if (element.measurements?.realWidth && element.measurements?.realLength) {
        // Use the actual realArea if available (accounts for curved edges), otherwise calculate from width*length
        const area =
          element.measurements.realArea ||
          element.measurements.realWidth * element.measurements.realLength
        const unitAbbrev = mapSettings.units === 'feet' ? 'ft' : 'm'
        const areaUnit = mapSettings.units === 'feet' ? 'sq ft' : 'sq m'

        ctx.font = '11px Arial'

        // For polygons, show approximate dimensions if available
        if (element.measurements.realWidth && element.measurements.realLength) {
          ctx.fillText(
            `~${element.measurements.realWidth.toFixed(
              1,
            )} × ${element.measurements.realLength.toFixed(1)} ${unitAbbrev}`,
            0,
            16,
          )
        }

        ctx.fillText(`${area.toFixed(1)} ${areaUnit}`, 0, 30)
      }
      ctx.restore()
    } else {
      const labelText = element.properties.roomName || element.properties.label || 'Polygon'

      // Draw white outline first
      ctx.strokeStyle = 'white'
      ctx.lineWidth = 4
      ctx.lineJoin = 'round'
      ctx.strokeText(labelText, labelX, labelY)

      // Then draw the fill text
      ctx.fillStyle = '#333'
      ctx.fillText(labelText, labelX, labelY)

      // Calculate and display area for polygon
      if (element.measurements?.realWidth && element.measurements?.realLength) {
        // Use the actual realArea if available (accounts for curved edges), otherwise calculate from width*length
        const area =
          element.measurements.realArea ||
          element.measurements.realWidth * element.measurements.realLength
        const unitAbbrev = mapSettings.units === 'feet' ? 'ft' : 'm'
        const areaUnit = mapSettings.units === 'feet' ? 'sq ft' : 'sq m'

        ctx.font = '11px Arial'

        // For polygons, show approximate dimensions if available
        if (element.measurements.realWidth && element.measurements.realLength) {
          ctx.fillText(
            `~${element.measurements.realWidth.toFixed(
              1,
            )} × ${element.measurements.realLength.toFixed(1)} ${unitAbbrev}`,
            labelX,
            labelY + 16,
          )
        }

        ctx.fillText(`${area.toFixed(1)} ${areaUnit}`, labelX, labelY + 30)
      }
    }
    ctx.textAlign = 'left'
  }
}

export {
  drawWallElement,
  drawDoorElement,
  drawWindowElement,
  drawRoomElement,
  drawPolygonElement,
}
